/**
 * One palette for every dot on every map.
 *
 * Why this file exists
 * --------------------
 * Two map components colored voter dots by engagement tier and **did not
 * agree**, in ways a coordinator comparing two screens would have to call a
 * bug:
 *
 *   InteractiveMap.tsx   anchor -> green,  super_voter -> gold
 *   VoterMapPanel.tsx    anchor -> red,    super_voter -> emerald
 *
 * Neither matched `glossary.ts`'s `TIER_PALETTE`, which is what the app's own
 * legend and Glossary page show users, and neither matched the design tokens.
 * Worse, `InteractiveMap`'s `anchor` branch was **unreachable**: the `hot`
 * branch above it tested the identical `score >= 60 && score < 75` band and
 * swallowed every anchor that reached it.
 *
 * That band was wrong too. The backend's `classify_tier` puts 75+ in the top
 * tier and 60–74 in `hot`; `anchor` and `super_voter` are two names for the
 * SAME 75–100 band (see `glossary.ts`: "Anchor / Super Voter"), not two tiers.
 *
 * So this module derives from `TIER_PALETTE` rather than restating hexes. The
 * palette a user reads in the Glossary is the palette the pins use, by
 * construction — which is the only version of "consistent" that survives
 * someone editing one of them later.
 *
 * Adding a third coloring scheme (knock status) to two components with two
 * palettes would have tripled the divergence, so the consolidation comes
 * first. See memory-bank/plans/route-autoplan-and-knock-map.md §3.4.
 */
import { TIER_PALETTE } from '@/lib/glossary';

// ---------------------------------------------------------------------------
// Engagement tier
// ---------------------------------------------------------------------------

/**
 * Tier for a voter, from the tier string when present and the score otherwise.
 *
 * Bands match `services/scoring.py::classify_tier` exactly. `super_voter` is
 * folded into `anchor` because they are the same 75–100 band under two names —
 * treating them as distinct is what produced a dead branch and two golds.
 */
export type TierKey = 'anchor' | 'hot' | 'warm' | 'cool';

export function tierFor(
  tier: string | undefined | null,
  score: number | undefined | null,
): TierKey {
  switch (tier) {
    case 'anchor':
    case 'super_voter':
      return 'anchor';
    case 'hot':
      return 'hot';
    case 'warm':
      return 'warm';
    case 'cool':
      return 'cool';
    default:
      break;
  }
  const s = typeof score === 'number' ? score : 0;
  if (s >= 75) return 'anchor';
  if (s >= 60) return 'hot';
  if (s >= 40) return 'warm';
  return 'cool';
}

export function tierColor(
  tier: string | undefined | null,
  score?: number | undefined | null,
): string {
  return TIER_PALETTE[tierFor(tier, score)].hex;
}

export const TIER_LEGEND: LegendEntry[] = (
  ['anchor', 'hot', 'warm', 'cool'] as TierKey[]
).map((k) => ({
  key: k,
  color: TIER_PALETTE[k].hex,
  label: TIER_PALETTE[k].label,
  detail: TIER_PALETTE[k].range,
  filled: true,
}));

// ---------------------------------------------------------------------------
// Knock status
// ---------------------------------------------------------------------------

/**
 * How a door stands, from the campaign's point of view.
 *
 * ⚠️ The three un-knocked states are DELIBERATELY different, and merging them
 * is the mistake this type exists to prevent. They all look like "grey, not
 * done yet" to a naive implementation, and each is a DIFFERENT action:
 *
 *   not_routed        -> plan a walk here
 *   other_route       -> nothing to do; it is on a walk you are not looking at
 *   routed_unknocked  -> staff the walk you already planned
 *
 * Collapsing them tells a coordinator their district is untouched when it is
 * fully planned and unstaffed — or sends them to re-plan turf another
 * candidate already covered.
 */
export type KnockState =
  | 'not_routed'
  | 'other_route'
  | 'routed_unknocked'
  | 'talked'
  | 'not_home'
  | 'refused'
  | 'moved';

export type LegendEntry = {
  key: string;
  color: string;
  label: string;
  detail?: string;
  /** false renders a hollow ring — shape carries meaning, not just hue. */
  filled: boolean;
  /** Optional glyph drawn inside the dot (currently only `moved`). */
  glyph?: string;
};

/**
 * Six hues alone are not colorblind-safe, and red/amber/green is the single
 * worst case for it. So fill and shape carry the distinction too: unplanned
 * doors are hollow rings, `moved` gets an ×. A legend is mandatory, not
 * optional — `KnockLegend` below, and every caller renders it.
 */
export const KNOCK_STYLE: Record<KnockState, LegendEntry> = {
  not_routed: {
    key: 'not_routed',
    color: '#94a3b8',
    label: 'Not on a route',
    detail: 'Nobody is scheduled to knock here — plan a walk',
    filled: false,
  },
  other_route: {
    key: 'other_route',
    color: '#475569',
    label: 'On another route',
    detail:
      "Planned on a walk outside this view — another candidate's slate, or another canvasser's route",
    filled: false,
  },
  routed_unknocked: {
    key: 'routed_unknocked',
    color: '#60a5fa',
    label: 'On a route, not knocked',
    detail: 'Planned but not yet walked — staff the shift',
    filled: true,
  },
  talked: {
    key: 'talked',
    color: '#22c55e',
    label: 'Talked',
    detail: 'Someone answered the door',
    filled: true,
  },
  not_home: {
    key: 'not_home',
    color: '#f59e0b',
    label: 'Not home',
    detail: 'Knocked, nobody in — worth a second pass',
    filled: true,
  },
  refused: {
    key: 'refused',
    color: '#ef4444',
    label: 'Refused',
    detail: 'Declined to talk',
    filled: true,
  },
  moved: {
    key: 'moved',
    color: '#6b7280',
    label: 'Moved',
    detail: 'Voter no longer lives here',
    filled: true,
    glyph: '×',
  },
};

export const KNOCK_LEGEND: LegendEntry[] = [
  KNOCK_STYLE.not_routed,
  KNOCK_STYLE.other_route,
  KNOCK_STYLE.routed_unknocked,
  KNOCK_STYLE.talked,
  KNOCK_STYLE.not_home,
  KNOCK_STYLE.refused,
  KNOCK_STYLE.moved,
];

export function knockStyle(state: KnockState | undefined | null): LegendEntry {
  // An unknown state falls back to `not_routed`, which is also what a voter
  // absent from the overlay response means — the endpoint only returns doors
  // it has something to say about.
  return KNOCK_STYLE[(state as KnockState) ?? 'not_routed'] ?? KNOCK_STYLE.not_routed;
}

// ---------------------------------------------------------------------------
// Marker HTML
// ---------------------------------------------------------------------------

/**
 * The inner HTML of a map dot. Returned as a string because both maps build
 * Leaflet `divIcon`s, and keeping the markup here is what stops the two from
 * drifting again the way their colors did.
 *
 * `size` is the dot diameter in px. Hollow entries render as a ring with a
 * transparent middle so they read differently from filled ones in grayscale.
 */
export function dotHtml(entry: LegendEntry, size = 14): string {
  const border = entry.filled
    ? '2px solid rgba(15, 23, 42, 0.9)'
    : `2px solid ${entry.color}`;
  const bg = entry.filled ? entry.color : 'transparent';
  const glyph = entry.glyph
    ? `<span style="color:#fff;font-size:${Math.round(size * 0.7)}px;line-height:1;font-weight:700;">${entry.glyph}</span>`
    : '';
  return `
      <div style="
        background-color: ${bg};
        border: ${border};
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 0 1px rgba(255,255,255,0.35), 0 1px 3px rgba(0,0,0,0.5);
      ">${glyph}</div>
    `;
}

/**
 * Cluster bubble markup, colored by the dominant state of its children.
 *
 * Not cosmetic. Both maps use `MarkerClusterGroup`, and at the default zoom of
 * 10 nearly everything on screen IS a cluster — so a cluster left in Leaflet's
 * default blue hides the entire visualization behind uniform bubbles. Whatever
 * the dots are colored by, the clusters have to carry it, or the first thing
 * anyone sees says nothing.
 */
export function clusterHtml(color: string, count: number): string {
  const size = count >= 1000 ? 48 : count >= 100 ? 40 : 34;
  return `
      <div style="
        background-color: ${color};
        border: 2px solid rgba(255,255,255,0.85);
        border-radius: 50%;
        width: ${size}px;
        height: ${size}px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #0f172a;
        font-size: ${count >= 1000 ? 12 : 13}px;
        font-weight: 700;
        box-shadow: 0 2px 6px rgba(0,0,0,0.45);
      ">${count >= 1000 ? `${Math.round(count / 100) / 10}k` : count}</div>
    `;
}

/** The most common entry among a cluster's children, ties broken by legend order. */
export function dominant(
  entries: LegendEntry[],
  order: LegendEntry[],
): LegendEntry | null {
  if (!entries.length) return null;
  const counts = new Map<string, number>();
  for (const e of entries) counts.set(e.key, (counts.get(e.key) ?? 0) + 1);
  let best: LegendEntry | null = null;
  let bestCount = -1;
  for (const candidate of order) {
    const c = counts.get(candidate.key) ?? 0;
    if (c > bestCount) {
      bestCount = c;
      best = candidate;
    }
  }
  return best;
}
